import { type Query, infiniteQueryOptions } from '@tanstack/react-query';

import { fetchApi } from '@/services/api-client';

import { type WindowDays, windowQuery } from './evidence-window';
import type {
  AttentionResponse,
  BehaviorResponse,
  Classroom,
  LearningReport,
  LessonListItem,
  LessonReport,
  LessonStudentVisits,
  SparkyRange,
} from './sparky-types';
import type { LiveBoard } from './use-sparky-realtime';

export type LessonListStatus = 'active' | 'draft' | 'ended' | 'all';

export function classroomQuery(viewerId: string, range: SparkyRange) {
  return {
    queryKey: ['sparky-classroom', viewerId, range],
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      fetchApi<Classroom>(`/sparky/classroom?${new URLSearchParams({ ...range })}`, { signal }),
    retry: false,
  };
}

export function liveQuery(viewerId: string) {
  return {
    queryKey: ['sparky-live', viewerId],
    queryFn: ({ signal }: { signal: AbortSignal }) => fetchApi<LiveBoard>('/sparky/live', { signal }),
    // Rows arrive as pushes; this only catches a push lost while the socket was quietly down.
    refetchInterval: 60_000,
    retry: false,
  };
}

export function lessonsQuery(viewerId: string, filter: { playerId?: string; status?: LessonListStatus } = {}) {
  const params = new URLSearchParams();
  if (filter.playerId) params.set('playerId', filter.playerId);
  if (filter.status) params.set('status', filter.status);
  const search = params.toString();
  return {
    queryKey: ['sparky-lessons', viewerId, filter.playerId, filter.status],
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      fetchApi<LessonListItem[]>(`/sparky/lessons${search ? `?${search}` : ''}`, { signal }),
    retry: false,
  };
}

/** While another evidence window loads, the same report keeps showing; `key` is its key without the window. */
function sameReport<T>(key: readonly unknown[]) {
  return (previous: T | undefined, query: Query<T, Error, T, readonly unknown[]> | undefined) =>
    query && JSON.stringify(query.queryKey.slice(0, key.length)) === JSON.stringify(key) ? previous : undefined;
}

/** The lesson's results; `window` overrides the school year its check-up first answers are counted in. */
export function lessonReportQuery(viewerId: string, lessonId: string | undefined, window?: WindowDays | null) {
  const search = new URLSearchParams(windowQuery(window)).toString();
  return {
    queryKey: ['sparky-lesson', viewerId, lessonId, ...(window ? [window] : [])],
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      fetchApi<LessonReport>(`/sparky/lessons/${encodeURIComponent(lessonId!)}${search ? `?${search}` : ''}`, {
        signal,
      }),
    placeholderData: sameReport<LessonReport>(['sparky-lesson', viewerId, lessonId]),
    enabled: Boolean(lessonId),
    retry: false,
  };
}

/** One member's attempts in a lesson, loaded when the teacher opens that student's detail. */
export function lessonStudentVisitsQuery(viewerId: string, lessonId: string, playerId: string, enabled: boolean) {
  return {
    queryKey: ['sparky-lesson-visits', viewerId, lessonId, playerId],
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      fetchApi<LessonStudentVisits>(
        `/sparky/lessons/${encodeURIComponent(lessonId)}/students/${encodeURIComponent(playerId)}/visits`,
        { signal }
      ),
    enabled,
    retry: false,
  } as const;
}

export function studentReportQuery(viewerId: string, playerId: string, range: SparkyRange, window?: WindowDays | null) {
  return {
    queryKey: ['sparky-report', viewerId, playerId, range, ...(window ? [window] : [])],
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      fetchApi<LearningReport>(
        `/sparky/reports/${encodeURIComponent(playerId)}?${new URLSearchParams({ ...range, ...windowQuery(window) })}`,
        { signal }
      ),
    placeholderData: sameReport<LearningReport>(['sparky-report', viewerId, playerId, range]),
    retry: false,
  } as const;
}

export function behaviorQuery(viewerId: string, range: SparkyRange, enabled = true) {
  return {
    queryKey: ['sparky-behavior', viewerId, range.from, range.to],
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      fetchApi<BehaviorResponse>(`/sparky/behavior?${new URLSearchParams({ from: range.from, to: range.to })}`, {
        signal,
      }),
    enabled,
    retry: false,
  };
}

/** Observations newest first, a page at a time; `before` is the cursor the previous page ended on. `followUps` pins open follow-ups. */
export function attentionQuery(viewerId: string, range: SparkyRange, followUps = false) {
  return infiniteQueryOptions({
    queryKey: ['sparky-attention', viewerId, range.from, range.to, followUps],
    initialPageParam: '',
    getNextPageParam: (page: AttentionResponse) => page.nextCursor ?? undefined,
    queryFn: ({ signal, pageParam }) => {
      const params = new URLSearchParams({ from: range.from, to: range.to });
      if (pageParam) params.set('before', pageParam);
      else if (followUps) params.set('followUps', '1');
      return fetchApi<AttentionResponse>(`/sparky/attention?${params}`, { signal });
    },
    retry: false,
  });
}

/** The viewer's open follow-ups on their own students over the last school days: the Sparky nav badge. */
export function followUpCountQuery(viewerId: string) {
  return {
    queryKey: ['sparky-follow-ups', viewerId],
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      fetchApi<{ count: number }>('/sparky/attention/follow-ups', { signal }),
    refetchInterval: 60_000,
    retry: false,
  };
}
